import { FC, useMemo } from 'react';
import {
  Button,
  ListTotal,
  TableToolbar,
  TableWrapper,
  Table,
  Typography,
  ActionsMenu,
} from 'melp-design/components';
import { Divider, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useRouteParams } from 'utils/useIdParam';
import { APP, ROUTES } from 'config';
import { useModalContext } from 'store/modal';
import { useConfirm } from 'store/confirm';
import { usePredefinedToasts } from 'utils/Toast';
import {
  CustomCategoryValuesFilter,
  EmployeesFilter,
  FilterButton,
  Filters,
  JobTitlesFilter,
  OrgStructureFilter,
  SelectFilter,
} from 'components/filters';
import { employeeInGroupStatuses } from 'store/employee-groups';
import { useSearchParams } from 'utils/navigation';
import {
  COLOR_BY_STATUS,
  assignmentStatuses,
  employeesByEmployeeGroupFiltersSchema,
  useAssignEmployeesToGroup,
  useEmployeesByEmployeeGroupAssignments,
} from 'store/assignments';
import { AccessTime } from '@mui/icons-material';
import { COLOR_BY_EMPLOYEE_STATUS, employeeStatuses } from 'store/employees';
import { EmployeeEmployeeGroupAssignmentHistory } from 'containers/assignments';
import { CompanyScopeInfoMessage } from 'containers/employees';
import { useMe } from 'state/Administrators';
import { EmployeeCell } from 'components/table';
import { CustomCategorySelectModal } from './atoms';

export const EmployeesGroupEmployeesAssignments: FC = () => {
  const { t } = useTranslation();
  const { id: employeeGroupId } = useRouteParams(ROUTES.employeeGroups.details);
  const { openModal } = useModalContext();
  const { confirmation } = useConfirm();
  const predefinedToasts = usePredefinedToasts();
  const { canEditGroups, hasEditPermission, hasViewPermission } = useMe();

  const { searchParams: filters, navigate } = useSearchParams(
    employeesByEmployeeGroupFiltersSchema,
  );
  const { selectedRowIds, setSelectedRowIds } = Table.useSelectedRows();

  const { data: assignments, isLoading } =
    useEmployeesByEmployeeGroupAssignments(employeeGroupId, filters);

  const { mutate: assignEmployeesToGroup, isLoading: isAssigning } =
    useAssignEmployeesToGroup();

  const selectedCategory = useMemo(() => {
    return (
      assignments?.customCategories.find(
        (c) => c.id === filters.customCategory,
      ) ?? null
    );
  }, [assignments, filters.customCategory]);

  return (
    <TableWrapper>
      <TableToolbar sticky>
        <Stack gap="10px">
          <CompanyScopeInfoMessage />

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            gap="15px"
          >
            <ActionsMenu
              type="spread"
              items={[
                {
                  label: t('employees.choose_custom_column'),
                  color: 'primary',
                  onClick: async () => {
                    const response = await openModal({
                      component: CustomCategorySelectModal,
                      props: {
                        options: assignments?.customCategories ?? [],
                        selected: filters.customCategory,
                      },
                    });
                    if (response.action === 'CONFIRM') {
                      navigate({
                        params: {
                          customCategory: response.category,
                          customCategoryValue: [],
                        },
                      });
                    }
                  },
                },
              ]}
            />
          </Stack>

          <Divider />

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap="10px"
          >
            <ListTotal total={assignments?.total} />

            <Stack direction="row" alignItems="center" gap="10px">
              <Filters
                value={filters}
                initialValues={employeesByEmployeeGroupFiltersSchema.parse({})}
                onChange={(v) => navigate({ params: { ...v, page: 1 } })}
                onChangeCallback={() => setSelectedRowIds({})}
              >
                {({ value, initialValues, setFilter }) => (
                  <>
                    {hasViewPermission(['employees']) ? (
                      <FilterButton
                        label={t('entity_type.employee')}
                        value={value.employees}
                        initialValue={initialValues.employees}
                        onChange={(v) => setFilter('employees', v)}
                      >
                        {({ value, applyFilter, clearFilter, close }) => (
                          <EmployeesFilter
                            value={{ value }}
                            onApplyFilter={({ value }) => applyFilter(value)}
                            onClearFilter={clearFilter}
                            onCancel={close}
                          />
                        )}
                      </FilterButton>
                    ) : null}

                    <FilterButton
                      label={t('common.copywriter')}
                      value={value.jobTitle}
                      initialValue={initialValues.jobTitle}
                      onChange={(v) => setFilter('jobTitle', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <JobTitlesFilter
                          value={{ value }}
                          onApplyFilter={({ value }) => applyFilter(value)}
                          onClearFilter={clearFilter}
                          onCancel={close}
                        />
                      )}
                    </FilterButton>

                    <FilterButton
                      label={t('employees.employee_status.label')}
                      value={value.employeeStatus}
                      initialValue={initialValues.employeeStatus}
                      onChange={(v) => setFilter('employeeStatus', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <SelectFilter
                          value={{ value }}
                          onApplyFilter={({ value }) => applyFilter(value)}
                          onClearFilter={clearFilter}
                          onCancel={close}
                          multiple
                          options={employeeStatuses.map((status) => ({
                            key: status,
                            label: t(`status.${status}`),
                          }))}
                          searchable={false}
                        />
                      )}
                    </FilterButton>

                    {filters.customCategory && selectedCategory ? (
                      <FilterButton
                        label={selectedCategory.name}
                        value={value.customCategoryValue}
                        initialValue={initialValues.customCategoryValue}
                        onChange={(v) => setFilter('customCategoryValue', v)}
                      >
                        {({
                          value: filterValue,
                          applyFilter,
                          clearFilter,
                          close,
                        }) => (
                          <CustomCategoryValuesFilter
                            value={{ value: filterValue }}
                            onApplyFilter={({ value }) => applyFilter(value)}
                            onClearFilter={clearFilter}
                            onCancel={close}
                            customCategoryId={value.customCategory}
                          />
                        )}
                      </FilterButton>
                    ) : null}

                    <FilterButton
                      label={t('home.organisationStructure_title')}
                      value={value.orgStructure}
                      initialValue={initialValues.orgStructure}
                      onChange={(v) => setFilter('orgStructure', v)}
                      contentMaxWidth={420}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <OrgStructureFilter
                          value={value}
                          onApplyFilter={applyFilter}
                          onClearFilter={clearFilter}
                          onCancel={close}
                        />
                      )}
                    </FilterButton>

                    <FilterButton
                      label={t('alternatives.assignmentStatus')}
                      value={value.assignmentStatus}
                      initialValue={initialValues.assignmentStatus}
                      onChange={(v) => setFilter('assignmentStatus', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <SelectFilter
                          value={{ value }}
                          onApplyFilter={({ value }) => applyFilter(value)}
                          onClearFilter={clearFilter}
                          onCancel={close}
                          options={assignmentStatuses.map((status) => ({
                            key: status,
                            label: t(`status.${status}`),
                          }))}
                          searchable={false}
                          multiple={true}
                        />
                      )}
                    </FilterButton>
                  </>
                )}
              </Filters>
            </Stack>
          </Stack>
        </Stack>
      </TableToolbar>

      <Table
        data={assignments?.items ?? []}
        isLoading={isLoading}
        columns={[
          {
            id: 'fullName',
            header: t('common.full_name'),
            cell: ({ row: { original: employee } }) => {
              return <EmployeeCell employee={employee} />;
            },
          },
          {
            id: 'customCategory',
            header: selectedCategory?.name,
            cell: ({ row: { original: employee } }) => {
              return (
                employee.customCategories.find(
                  (c) => c.id === selectedCategory?.id,
                )?.value.name ?? ''
              );
            },
          },
          {
            id: 'jobTitle',
            header: t('common.copywriter'),
            cell: ({ row: { original: employee } }) => {
              return employee.jobTitle;
            },
          },
          {
            id: 'status',
            header: t('employees.employee_status.label'),
            cell: ({ row: { original: employee } }) => {
              return (
                <Typography color={COLOR_BY_EMPLOYEE_STATUS[employee.status]}>
                  {t(`status.${employee.status}`)}
                </Typography>
              );
            },
          },
          {
            id: 'assignmentStatus',
            header: t('alternatives.assignmentStatus'),
            meta: { align: 'center' },
            cell: ({ row: { original: assignment } }) => {
              return (
                <Stack
                  flexDirection="row"
                  gap={1.5}
                  alignItems="center"
                  justifyContent="center"
                >
                  <Typography
                    color={COLOR_BY_STATUS[assignment.assignmentStatus]}
                  >
                    {t(`status.${assignment.assignmentStatus}`)}
                  </Typography>

                  {assignment.assignmentCount ? (
                    <Button
                      variant="neutral-outline"
                      size="sm"
                      icon={AccessTime}
                      onClick={() => {
                        openModal({
                          component: EmployeeEmployeeGroupAssignmentHistory,
                          props: {
                            employeeGroupId,
                            employeeId: assignment.id,
                            readonly:
                              !canEditGroups || !hasEditPermission(['groups']),
                          },
                          size: 'lg',
                        });
                      }}
                      tooltip={t('permissions.historyModule')}
                    />
                  ) : null}
                </Stack>
              );
            },
          },
        ]}
        pagination={
          assignments && {
            count: assignments.total,
            page: assignments.page,
            pageSize: assignments.pageSize,
            pageSizeOptions: APP.pagination.defaults.pageSize,
            onChange: (page, pageSize) => {
              navigate({ params: { page, pageSize } });
            },
          }
        }
        rows={{
          inactive: ({ original: assignment }) =>
            assignment.assignmentStatus === 'off',
        }}
        selection={{
          value: selectedRowIds,
          onChange: setSelectedRowIds,
          actions: employeeInGroupStatuses.map((status) => ({
            label: t(`status.mark_selections_as_${status}`),
            disabled: !canEditGroups || !hasEditPermission(['groups']),
            onClick: async () => {
              const { confirmed } = await confirmation('');

              if (confirmed) {
                assignEmployeesToGroup(
                  {
                    employeeGroupId,
                    ...(status === 'included'
                      ? {
                          action: 'create',
                          data: {
                            employeeIds: Object.keys(selectedRowIds),
                            fromNow: true,
                          },
                        }
                      : {
                          action: 'update',
                          data: {
                            employeeIds: Object.keys(selectedRowIds),
                            toNow: true,
                          },
                        }),
                  },
                  {
                    onSuccess: () => {
                      setSelectedRowIds({});
                      predefinedToasts.success.updated();
                    },
                  },
                );
              }
            },
          })),
          isLoading: isAssigning,
        }}
      />
    </TableWrapper>
  );
};
